import { Badge } from '@latitude/badge';
import { ButtonStack } from '@latitude/button-stack';
import { CircularProgress } from '@latitude/circular-progress';
import { Pagination } from '@latitude/pagination';
import { cn } from '@latitude/token-utils';
import { Tooltip, TooltipProvider } from '@latitude/tooltip';

import { StatusItem } from './StatusItem';
import type { FloatingToolbarProps } from './types';

const FloatingToolbar = ({
  leading,
  statuses,
  centerSlot,
  badges,
  trailingButtonStack,
  className,
  'data-testid': testId = 'floating-toolbar',
}: FloatingToolbarProps) => {
  const hasBadges = badges && badges.length > 0;
  const hasTrailing = hasBadges || !!trailingButtonStack;
  const hasCenter = !!statuses?.length || centerSlot != null;

  return (
    <div
      className={cn(
        'flex items-center gap-4 rounded-lg p-[14px]',
        'bg-color-ref-ui-background-container',
        'border-2 border-color-ref-ui-divider-strong',
        'shadow-box-shadow-sys-medium',
        className,
      )}
      data-testid={testId}
    >
      {leading && (
        <div
          className="flex shrink-0 items-center gap-2"
          data-testid={`${testId}-leading`}
        >
          {leading.type === 'buttonStack' && (
            <ButtonStack
              {...leading.props}
              data-testid={
                leading.props['data-testid'] ?? `${testId}-leading-button-stack`
              }
            />
          )}
          {leading.type === 'pagination' && (
            <div data-testid={`${testId}-pagination`}>
              <Pagination {...leading.props} />
            </div>
          )}
          {leading.type === 'progress' && (
            <div data-testid={`${testId}-progress`}>
              <CircularProgress {...leading.props} />
            </div>
          )}
        </div>
      )}

      {hasCenter && (
        <div
          className="flex min-w-0 flex-1 items-center gap-4"
          data-testid={`${testId}-center`}
        >
          {!!statuses?.length && (
            <TooltipProvider>
              <div
                className="flex min-w-0 items-center gap-4"
                data-testid={`${testId}-statuses`}
              >
                {statuses.map((status, index) => {
                  const statusTestId =
                    status['data-testid'] ?? `${testId}-status-${index}`;
                  return (
                    <StatusItem
                      key={statusTestId}
                      {...status}
                      data-testid={statusTestId}
                    />
                  );
                })}
              </div>
            </TooltipProvider>
          )}
          {centerSlot != null && (
            <div data-testid={`${testId}-center-slot`}>{centerSlot}</div>
          )}
        </div>
      )}

      {hasTrailing && (
        <div
          className="flex shrink-0 items-center gap-4"
          data-testid={`${testId}-trailing`}
        >
          {hasBadges && (
            <TooltipProvider>
              <div
                className="flex items-center gap-2"
                data-testid={`${testId}-badges`}
              >
                {badges.map(({ tooltip, ...badge }, index) => {
                  const badgeTestId =
                    badge['data-testid'] ?? `${testId}-badge-${index}`;
                  const badgeElement = (
                    <Badge {...badge} data-testid={badgeTestId} />
                  );
                  return tooltip ? (
                    <Tooltip key={badgeTestId} text={tooltip}>
                      {badgeElement}
                    </Tooltip>
                  ) : (
                    <span key={badgeTestId}>{badgeElement}</span>
                  );
                })}
              </div>
            </TooltipProvider>
          )}
          {trailingButtonStack && (
            <ButtonStack
              {...trailingButtonStack}
              data-testid={
                trailingButtonStack['data-testid'] ??
                `${testId}-trailing-button-stack`
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

FloatingToolbar.displayName = 'FloatingToolbar';

export { FloatingToolbar };
